// Friends (/friends, wave 13) and bringing your people over from X (/friends/x). Signed out, Friends sells the
// network with the same copy as the launcher (docs/build/people.md section 1). Signed in: who's playing (with Join),
// requests, suggestions with one-tap Accept all, and your friends. The X import runs on social's /v1/x routes: link
// X by posting a code, get the archive (X can take a day), upload it, see what came back and what's waiting, undo.

import { accessToken, api, API_BASE, ApiError } from "../api.ts";
import { clear, errorBox, h, link, toast } from "../dom.ts";
import { setTitle } from "../router.ts";
import { currentMe, signedIn } from "../session.ts";
import { avatar, initial, peopleError, peopleStyles, playingStrip, type Playing } from "../people.ts";
import { userName } from "./game.ts";
import { squarePortrait, xProfileFromZip, type XProfile } from "../x-profile.ts";

interface Presence {
  user_id: string;
  name: string | null;
  status: "online" | "away" | "in_game" | "offline";
  game_id: string | null;
  joinable: boolean;
  party_id: string | null;
  /** their avatar portrait (BANGERS.md slice 4) */
  picture?: string | null;
}

/** An avatar and a name for a user id, filled in once accounts answers. */
function who(id: string): HTMLElement[] {
  const a = avatar(null, "small");
  const n = h("span", { class: "who" }, "Player");
  void userName(id).then((name) => {
    n.textContent = name;
    a.textContent = initial(name);
  });
  return [a, n];
}

/** "Accept" for one, "Accept all (n)" for more. */
const acceptAll = (n: number) => (n === 1 ? "Accept" : `Accept all (${n.toLocaleString("en")})`);

const signin = (next: string) => `/signin?next=${encodeURIComponent(next)}`;

// ---- signed out ----

const HUES = ["#ff5a7a", "#2b9bff", "#ffb02b", "#6fd39a"];
const MOCK: [string, string, boolean][] = [
  ["Mara", "Playing Punk Drive", true],
  ["Okoye", "In the Plaza", true],
  ["Teo", "Online", false],
  ["Wren", "Last played Dystopia · 2 h ago", false],
];

function pitch(): HTMLElement {
  const card = (title: string, body: string) => h("div", { class: "pitch-card" }, h("h3", null, title), h("p", null, body));
  const step = (n: number, title: string, body: string) => h("li", { class: "x-step" }, h("span", { class: "x-num", "aria-hidden": "true" }, String(n)), h("div", null, h("strong", null, title), h("p", { class: "dim small" }, body)));
  return h(
    "div",
    { class: "people friends-out" },
    h(
      "section",
      { class: "pitch-hero" },
      h(
        "div",
        { class: "pitch-copy" },
        h("p", { class: "eyebrow" }, "Friends"),
        h("h1", null, "Games are better with your people."),
        h("p", { class: "lead" }, "See who's online, what they're playing, and jump into their game with one click."),
        h("div", { class: "actions" }, link(signin("/friends"), { class: "btn" }, "Sign in"), link(signin("/friends"), { class: "text-link" }, "New here? Create an account")),
      ),
      h(
        "ul",
        { class: "mock-friends", "aria-label": "An example friends list" },
        MOCK.map(([name, line, live], i) => h("li", { class: "mock-row" }, h("span", { class: `dot${live ? " on" : ""}`, "aria-hidden": "true" }), Object.assign(avatar(name, "small"), { style: `background:${HUES[i]}` }), h("div", { class: "mock-who" }, h("strong", null, name), h("span", { class: "dim small" }, line)), live ? h("span", { class: "btn small ghost", "aria-hidden": "true" }, "Join") : null)),
      ),
    ),
    h(
      "section",
      { class: "pitch-cards" },
      card("See what they're playing", "Your friends' status updates live: online, in a game, in the Plaza. Only friends see yours, and you can go invisible any time."),
      card("Join in one click", "When a friend's game is open to join, hit Join. Banger starts the same game and puts you in their party, up to eight of you."),
      card("Follow the people who make games", "Follow developers and players to see what they're building, their new releases and what they post."),
    ),
    h(
      "section",
      { class: "x-band" },
      h("p", { class: "eyebrow" }, "Only on Banger"),
      h("h2", null, "Bring your people from X."),
      h("p", { class: "lead" }, "Link your X account and upload your X archive. Anyone you know from X who has already brought their X here, you're connected with again. Everyone else is kept waiting: when they join and bring theirs, you connect automatically."),
      h(
        "ol",
        { class: "x-steps" },
        step(1, "Link X.", "Post a short code from your account."),
        step(2, "Upload your archive.", "We show you where to get it. X can take a day to send it."),
        step(3, "You're connected.", "People who've brought their X here are followed again right away. Mutuals become friend suggestions you accept in one tap."),
      ),
      h("p", { class: "fine" }, "We only read who you follow and who follows you. People who haven't joined are stored as scrambled IDs, never names. Undo removes everything that came from X."),
      link(signin("/friends/x"), { class: "btn" }, "Sign in to bring your people"),
    ),
    h("section", { class: "pitch-foot" }, h("p", null, "Your friends list is waiting."), link(signin("/friends"), { class: "btn" }, "Sign in")),
  );
}

// ---- signed in ----

const statusLine = (p: Presence) => (p.status === "in_game" ? "In a game" : p.status === "online" ? "Online" : p.status === "away" ? "Away" : "Offline");

export function friendsPage(): HTMLElement {
  peopleStyles();
  setTitle("Friends");
  if (!signedIn()) return pitch();
  const wrap = h("div", { class: "people friends-in" }, h("p", { class: "dim" }, "Loading…"));
  const draw = async () => {
    try {
      const [pres, reqs, sugg, x] = await Promise.all([
        api<{ friends: Presence[] }>("social", "/presence/friends"),
        api<{ incoming: { user_id: string }[]; outgoing: { user_id: string }[] }>("social", "/friends/requests"),
        api<{ suggestions: { user_id: string; kind: "friend" | "follow" }[] }>("social", "/friends/suggestions"),
        api<{ linked: boolean }>("social", "/x/link").catch(() => ({ linked: false })),
      ]);
      const playing: Playing[] = pres.friends.filter((f) => f.status === "in_game" && f.game_id).map((f) => ({ user_id: f.user_id, name: f.name, game_id: f.game_id!, joinable: f.joinable, party_id: f.party_id }));
      const act = (label: string, path: string, method = "POST", cls = "btn small") =>
        h(
          "button",
          {
            type: "button",
            class: cls,
            onclick: async () => {
              try {
                await api("social", path, { method });
                await draw();
              } catch (e) {
                toast(peopleError(e));
              }
            },
          },
          label,
        );
      const order = { in_game: 0, online: 1, away: 2, offline: 3 } as const;
      const friends = [...pres.friends].sort((a, b) => order[a.status] - order[b.status]);
      clear(
        wrap,
        h("header", { class: "people-head" }, h("h1", null, "Friends"), h("nav", { class: "people-tabs" }, link("/friends", { class: "tab", "aria-current": "page" }, "Friends"), link("/friends/x", { class: "tab" }, "From X"), link("/feed", { class: "tab" }, "Feed"))),
        playingStrip(playing),
        reqs.incoming.length
          ? h("section", { class: "section" }, h("h2", null, "Friend requests"), h("ul", { class: "people-list" }, reqs.incoming.map((r) => h("li", { class: "person" }, who(r.user_id), h("span", { class: "topbar-fill" }), h("span", { class: "person-acts" }, act("Accept", `/friends/requests/${r.user_id}/accept`), act("Decline", `/friends/requests/${r.user_id}`, "DELETE", "btn small ghost"))))))
          : null,
        sugg.suggestions.length
          ? h(
              "section",
              { class: "section" },
              h("div", { class: "row-head" }, h("h2", null, `People you know from X · ${sugg.suggestions.length}`), act(acceptAll(sugg.suggestions.length), "/friends/suggestions/accept-all")),
              h("p", { class: "dim small" }, "Mutuals become friend requests; the rest become follows. Nothing happens until you tap."),
              h("ul", { class: "people-list" }, sugg.suggestions.map((s) => h("li", { class: "person" }, who(s.user_id), h("span", { class: "chip" }, s.kind === "friend" ? "Mutual" : "Follow"), h("span", { class: "topbar-fill" }), h("span", { class: "person-acts" }, act("Accept", `/friends/suggestions/${s.user_id}/accept`), act("Dismiss", `/friends/suggestions/${s.user_id}`, "DELETE", "btn small ghost"))))),
            )
          : null,
        h(
          "section",
          { class: "section" },
          h("h2", null, friends.length ? `Your friends · ${friends.length}` : "Your friends"),
          friends.length
            ? h("ul", { class: "people-list" }, friends.map((f) => h("li", { class: "person" }, h("span", { class: `dot${f.status === "offline" ? "" : " on"}`, "aria-hidden": "true" }), avatar(f.name, "small", f.picture ?? null), h("div", { class: "mock-who" }, h("strong", null, f.name ?? "Friend"), h("span", { class: "dim small" }, statusLine(f))))))
            : h("div", { class: "panel" }, h("h3", null, "No friends here yet"), h("p", null, "Friends you add in the Banger app, or bring over from X, show up here."), link("/friends/x", { class: "btn" }, "Bring your people from X")),
        ),
        reqs.outgoing.length ? h("p", { class: "dim small" }, `${reqs.outgoing.length} request${reqs.outgoing.length === 1 ? "" : "s"} waiting for an answer.`) : null,
        !x.linked ? h("aside", { class: "x-nudge" }, h("strong", null, "Bring your people from X."), h("span", { class: "dim" }, " Anyone who has already brought their X here is one tap away."), link("/friends/x", { class: "btn small" }, "Start")) : null,
      );
    } catch (e) {
      clear(wrap, errorBox(e));
    }
  };
  void draw();
  return wrap;
}

// ---- the X import ----

interface Summary {
  uploaded_at: number;
  counts: { following: number; followers: number };
  trust: number;
  pending: number;
  restored: { following: string[]; followers: string[]; friends: string[]; friend_suggestions: string[]; follow_suggestions: string[] };
  truncated?: boolean;
}
interface Code {
  code: string;
  tweet_text: string;
  expires_at: number;
}

/** The local stack's social answers its dev confirm route (400 on an empty body); everywhere else it's a 404. */
let devStack: Promise<boolean> | null = null;
const isDevStack = () =>
  (devStack ??= fetch(`${API_BASE.social}/dev/x/verify/confirm`, { method: "POST", headers: { "content-type": "application/json" }, body: "{}" }).then((r) => r.status === 400, () => false));

/** The archive picked on this page, kept so "Bring your X profile" can read it in the browser without asking again. */
let lastArchive: File | null = null;

async function uploadArchive(file: File): Promise<Summary> {
  lastArchive = file;
  const tok = await accessToken();
  const res = await fetch(`${API_BASE.social}/x/archive`, { method: "POST", headers: { authorization: `Bearer ${tok ?? ""}`, "content-type": "application/zip" }, body: file });
  const body = (await res.json().catch(() => null)) as (Summary & { error?: { code: string; message: string } }) | null;
  if (!res.ok) throw new ApiError(res.status, body?.error?.code ?? `http_${res.status}`, body?.error?.message ?? "The upload didn't work.");
  return body as Summary;
}

export function xImportPage(): HTMLElement {
  peopleStyles();
  setTitle("Bring your people from X");
  if (!signedIn()) return pitch();
  const wrap = h("div", { class: "people x-import" }, h("p", { class: "dim" }, "Loading…"));
  const head = h(
    "header",
    { class: "people-head" },
    h("h1", null, "Bring your people from X"),
    h("nav", { class: "people-tabs" }, link("/friends", { class: "tab" }, "Friends"), link("/friends/x", { class: "tab", "aria-current": "page" }, "From X"), link("/feed", { class: "tab" }, "Feed")),
  );
  const minor = currentMe()?.ageBand === "13-17";
  const steps = (active: 1 | 2 | 3) =>
    h(
      "ol",
      { class: "x-progress", "aria-label": "Steps" },
      [["Link X", "Link X"], ["Upload your archive", "Upload"], ["You're connected", "Connected"]].map(([t, short], i) => h("li", { class: i + 1 < active ? "done" : i + 1 === active ? "on" : "", "aria-current": i + 1 === active ? "step" : null }, h("span", { class: "x-num" }, String(i + 1)), h("span", { class: "x-long" }, t), h("span", { class: "x-short" }, short))),
    );

  const linkStep = async () => {
    const msg = h("div", { "aria-live": "polite" });
    const url = h("input", { name: "tweet_url", type: "url", placeholder: "https://x.com/you/status/…", "aria-label": "Link to your post" });
    const codeBox = h("div", { class: "x-code" }, h("button", { type: "button", class: "btn", onclick: () => void getCode() }, "Get my code"));
    const getCode = async () => {
      try {
        const c = await api<Code>("social", "/x/verify/start", { method: "POST" });
        clear(
          codeBox,
          h("p", null, "Post this from your X account (you can delete it once you're linked):"),
          h("pre", { class: "x-tweet" }, c.tweet_text),
          h("div", { class: "actions" }, h("a", { class: "btn", href: `https://x.com/intent/post?text=${encodeURIComponent(c.tweet_text)}`, target: "_blank", rel: "noopener" }, "Post it on X"), h("button", { type: "button", class: "btn ghost", onclick: () => void navigator.clipboard?.writeText(c.tweet_text).then(() => toast("Copied.")) }, "Copy")),
          h("form", { class: "x-check", onsubmit: (e: Event) => (e.preventDefault(), void check()) }, h("label", null, "Then paste the link to your post", url), h("button", { class: "btn", type: "submit" }, "Check")),
        );
        if (await isDevStack()) codeBox.append(devConfirm(c.code));
      } catch (e) {
        clear(msg, h("p", { class: "error small" }, peopleError(e)));
      }
    };
    const check = async () => {
      try {
        const r = await api<{ status: string }>("social", "/x/verify/check", { body: { tweet_url: (url as HTMLInputElement).value.trim() } });
        if (r.status === "verified") return void render();
        clear(msg, h("p", { class: "dim small", role: "status" }, "We couldn't see the code yet. X can be slow; try again in a minute."));
      } catch (e) {
        clear(msg, h("p", { class: "error small", role: "alert" }, peopleError(e)));
      }
    };
    const devConfirm = (code: string) => {
      const id = h("input", { name: "x_account_id", inputmode: "numeric", placeholder: "Numeric X account id", "aria-label": "X account id (dev)" });
      return h(
        "form",
        {
          class: "x-dev",
          onsubmit: async (e: Event) => {
            e.preventDefault();
            const res = await fetch(`${API_BASE.social}/dev/x/verify/confirm`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code, x_account_id: (id as HTMLInputElement).value.trim() }) });
            if (res.ok) void render();
            else clear(msg, h("p", { class: "error small" }, "The dev confirm didn't work."));
          },
        },
        h("span", { class: "chip" }, "Local dev"),
        h("span", { class: "dim small" }, "The tweet check is a stub here. Confirm as an X account id:"),
        id,
        h("button", { class: "btn small ghost", type: "submit" }, "Confirm"),
      );
    };
    return h("section", { class: "x-card" }, h("h2", null, "1. Link your X account"), h("p", { class: "dim" }, "Posting a code shows us the account is yours. We never ask for your X password and never post for you."), codeBox, msg);
  };

  const archiveStep = () => {
    const msg = h("div", { "aria-live": "polite" });
    const file = h("input", { type: "file", accept: ".zip,application/zip", "aria-label": "Your X archive (.zip)" });
    return h(
      "section",
      { class: "x-card" },
      h("h2", null, "2. Get your X archive and upload it"),
      h(
        "ol",
        { class: "x-help" },
        h("li", null, "On X, open ", h("strong", null, "Settings and privacy → Your account → Download an archive of your data"), "."),
        h("li", null, "Confirm your password and the code X sends you, then press ", h("strong", null, "Request archive"), "."),
        h("li", null, "X emails you when it's ready. ", h("strong", null, "It can take a day or more."), " Come back here then; your link stays."),
        h("li", null, "Download the .zip and pick it below. We read only who you follow and who follows you; your posts, messages and media are never opened."),
      ),
      h(
        "form",
        {
          class: "x-upload",
          onsubmit: async (e: Event) => {
            e.preventDefault();
            const f = (file as HTMLInputElement).files?.[0];
            if (!f) return void clear(msg, h("p", { class: "error small" }, "Pick the .zip X sent you."));
            clear(msg, h("p", { class: "dim small", role: "status" }, "Reading your archive…"));
            try {
              await uploadArchive(f);
              void render();
            } catch (x) {
              clear(msg, h("p", { class: "error small", role: "alert" }, peopleError(x)));
            }
          },
        },
        file,
        h("button", { class: "btn", type: "submit" }, "Upload"),
      ),
      msg,
      h("p", { class: "fine" }, "Too big? Send only data/follower.js and data/following.js from inside the zip, zipped on their own."),
    );
  };

  const plural = (x: number, word: string) => `${x.toLocaleString("en")} ${word}${x === 1 ? "" : "s"}`;
  const resultStep = (s: Summary, suggestions: number) => {
    const r = s.restored;
    const n = (x: number) => x.toLocaleString("en");
    const stat = (num: number, label: string) => h("div", { class: "x-stat" }, h("strong", null, n(num)), h("span", { class: "dim small" }, label));
    const undo = h(
      "button",
      {
        type: "button",
        class: "btn ghost",
        onclick: async () => {
          if (!confirm("Remove every follow, friend and suggestion that came from X, and delete your archive here?")) return;
          try {
            await api("social", "/x/import", { method: "DELETE" });
            toast("Your X import is undone.");
            void render();
          } catch (e) {
            toast(peopleError(e));
          }
        },
      },
      "Undo import",
    );
    return h(
      "section",
      { class: "x-card" },
      h("h2", null, "3. You're connected"),
      h("p", { class: "dim" }, `Your archive lists ${plural(s.counts.following, "account")} you follow and ${plural(s.counts.followers, "follower")}.`),
      h("div", { class: "x-stats" }, stat(r.following.length, "you follow again"), stat(r.followers.length, "follow you again"), stat(r.friends.length, "new friends"), stat(s.pending, "waiting to join")),
      suggestions
        ? h(
            "div",
            { class: "x-accept" },
            h("p", null, h("strong", null, suggestions === 1 ? "Someone you know from X is here." : `${n(suggestions)} people you know from X are here.`), minor ? " Accept the ones you want; nothing happens until you tap." : " Mutuals become friend requests, the rest become follows."),
            h(
              "button",
              {
                type: "button",
                class: "btn",
                onclick: async () => {
                  try {
                    const t = await api<{ requested: number; friends: number; followed: number }>("social", "/friends/suggestions/accept-all", { method: "POST" });
                    toast(`Done: ${t.friends} friends, ${t.requested} requests sent, ${t.followed} follows.`);
                    void render();
                  } catch (e) {
                    toast(peopleError(e));
                  }
                },
              },
              acceptAll(suggestions),
            ),
            link("/friends", { class: "text-link" }, "Pick one by one"),
          )
        : null,
      s.pending ? h("p", { class: "dim" }, `${s.pending === 1 ? "1 person" : `${n(s.pending)} people`} from your X ${s.pending === 1 ? "isn't" : "aren't"} here yet. When they join and bring their X, you connect automatically.`) : null,
      s.trust < 0.34 ? h("p", { class: "note small" }, "Your archive is small, so its matches come as suggestions rather than automatic follows.") : null,
      s.truncated ? h("p", { class: "note small" }, "Your archive is very large; we read the first 100,000 accounts of each list.") : null,
      h("div", { class: "actions" }, undo, h("label", { class: "btn ghost" }, "Upload a newer archive", h("input", { type: "file", accept: ".zip,application/zip", hidden: true, onchange: async (e: Event) => {
        const f = (e.target as HTMLInputElement).files?.[0];
        if (!f) return;
        try {
          await uploadArchive(f);
          toast("Archive updated.");
          void render();
        } catch (x) {
          toast(peopleError(x));
        }
      } }))),
      h("p", { class: "fine" }, "Undo removes every follow, friend and suggestion that came from X, and your archive. People who haven't joined are kept only as scrambled IDs."),
      xProfileCard(),
    );
  };

  const render = async () => {
    try {
      const xl = await api<{ linked: boolean }>("social", "/x/link");
      let summary: Summary | null = null;
      if (xl.linked) summary = await api<Summary>("social", "/x/import").catch((e) => (e instanceof ApiError && e.status === 404 ? null : Promise.reject(e)));
      if (!xl.linked) return clear(wrap, head, steps(1), await linkStep());
      if (!summary) return clear(wrap, head, steps(2), h("p", { class: "ok-line" }, "✓ Your X account is linked."), archiveStep());
      const sugg = await api<{ suggestions: { user_id: string }[] }>("social", "/friends/suggestions");
      clear(wrap, head, steps(3), resultStep(summary, new Set(sugg.suggestions.map((s) => s.user_id)).size));
    } catch (e) {
      clear(wrap, head, errorBox(e));
    }
  };
  void render();
  return wrap;
}

/**
 * "Bring your X profile too" (BANGERS.md slice 7): opt-in, after the import. This page opens the archive here, in the
 * browser, and reads only your bio, your X username and your profile picture. You pick which to use; nothing is saved
 * until you press Save.
 */
function xProfileCard(): HTMLElement {
  const body = h("div", { class: "x-profile-body" });
  const msg = h("div", { "aria-live": "polite" });
  const read = async (f: Blob) => {
    clear(msg, h("p", { class: "dim small", role: "status" }, "Reading your profile from the archive…"));
    try {
      show(await xProfileFromZip(f));
      clear(msg);
    } catch (e) {
      clear(msg, h("p", { class: "error small", role: "alert" }, e instanceof Error ? e.message : "That archive couldn't be read."));
    }
  };
  const pick = h("label", { class: "btn ghost" }, lastArchive ? "Pick a different archive" : "Pick your X archive", h("input", { type: "file", accept: ".zip,application/zip", hidden: true, onchange: (e: Event) => { const f = (e.target as HTMLInputElement).files?.[0]; if (f) void read(f); } }));
  const show = (p: XProfile) => {
    const box = (name: string, label: string, on: boolean) => h("label", { class: "x-profile-opt" }, h("input", { type: "checkbox", name, checked: on }), label);
    const picUrl = p.picture ? URL.createObjectURL(p.picture) : null;
    const xUrl = p.username ? `https://x.com/${p.username}` : null;
    if (!p.bio && !xUrl && !p.picture) return clear(body, h("p", { class: "dim" }, "Your archive has no bio, username or picture to bring."));
    const form = h(
      "form",
      {
        class: "x-profile-form",
        onsubmit: async (e: Event) => {
          e.preventDefault();
          const want = (n: string) => !!(form.querySelector(`input[name=${n}]`) as HTMLInputElement | null)?.checked;
          const done: string[] = [];
          try {
            const patch: Record<string, unknown> = {};
            if (want("bio") && p.bio) patch.bio = p.bio;
            if (want("link") && xUrl) {
              const card = (await api<{ card: { links: { label: string; url: string }[] } }>("social", "/profile")).card;
              if (!card.links.some((l) => l.url.replace(/\/$/, "").toLowerCase() === xUrl.toLowerCase())) patch.links = [...card.links, { label: "X", url: xUrl }].slice(0, 8);
            }
            if (Object.keys(patch).length) {
              await api("social", "/profile", { method: "PATCH", body: patch });
              done.push(Object.keys(patch).map((k) => (k === "bio" ? "your bio" : "a link to your X")).join(" and "));
            }
            if (want("picture") && p.picture) {
              await api("accounts", "/me/avatar/portrait", { method: "PUT", bytes: await squarePortrait(p.picture) });
              done.push("your picture");
            }
            toast(done.length ? `Added ${done.join(" and ")} to your profile.` : "Nothing changed.");
          } catch (x) {
            clear(msg, h("p", { class: "error small", role: "alert" }, peopleError(x)));
          }
        },
      },
      h("div", { class: "x-profile-preview" }, picUrl ? h("img", { src: picUrl, alt: "Your X picture", width: 72, height: 72 }) : null, h("div", null, p.displayName ? h("strong", null, p.displayName) : null, p.username ? h("p", { class: "dim small" }, `@${p.username} on X`) : null, p.bio ? h("p", null, p.bio) : null)),
      h("div", { class: "x-profile-opts" }, p.bio ? box("bio", "Use this bio", true) : null, xUrl ? box("link", "Add a link to my X", true) : null, p.picture ? box("picture", "Use this picture", true) : null),
      h("button", { class: "btn", type: "submit" }, "Save to my profile"),
    );
    clear(body, form);
  };
  if (lastArchive) clear(body, h("button", { type: "button", class: "btn ghost", onclick: () => void read(lastArchive!) }, "Read my profile from this archive"), pick);
  else clear(body, pick);
  return h(
    "section",
    { class: "x-profile" },
    h("h3", null, "Bring your X profile too?"),
    h("p", { class: "dim small" }, "We open your archive here, in your browser, and read only your bio, your X username and your profile picture. You choose what to use; nothing is saved until you press Save."),
    body,
    msg,
  );
}
